import React, { PureComponent } from 'react';
import { compose } from 'recompose';
import { connect } from 'react-redux';
import { defineMessages, injectIntl } from 'react-intl';
import PropTypes from 'prop-types';
import {
  asyncRequestContextsSelectors,
  asyncRequestContextsActions,
} from '@lifeomic/phc-web-toolkit/src/redux/modules/asyncRequestContexts';
import { IconButton } from '@lifeomic/chroma-react/components/IconButton';
import { Trash } from '@lifeomic/chromicons';
import { consentActions } from '@lifeomic/phc-web-toolkit/src/redux/modules/consent';
import { contextKeys } from '../../../../../redux/modules/asyncRequestContexts';
import NotificationHelper from '@lifeomic/phc-web-toolkit/src/redux/util/NotificationHelper';
import EnforceABAC, {
  permissions,
} from '@lifeomic/phc-web-toolkit/src/components/EnforceABAC';
import { Modal } from '@lifeomic/chroma-react/components/Modal';
import { Text } from '@lifeomic/chroma-react/components/Text';
import { Button } from '@lifeomic/chroma-react/components/Button';

const messages = defineMessages({
  titleProposed: {
    id: 'patientViewer.subjectConsent.deleteDialog.titleProposed',
    defaultMessage: 'Delete proposed consent?',
  },
  titleActive: {
    id: 'patientViewer.subjectConsent.deleteDialog.titleActive',
    defaultMessage: 'Retract consent?',
  },
  contentProposed: {
    id: 'patientViewer.subjectConsent.deleteDialog.contentProposed',
    defaultMessage:
      'Are you sure you want to delete the proposed consent "{name}" from {date}? This cannot be undone.',
  },
  contentActive: {
    id: 'patientViewer.subjectConsent.deleteDialog.contentActive',
    defaultMessage:
      'Are you sure you want to retract the accepted consent "{name}" from {date}? This cannot be undone.',
  },
  acceptButtonText: {
    id: 'patientViewer.subjectConsent.deleteDialog.accept',
    defaultMessage: 'Remove',
  },
  cancelButtonText: {
    id: 'patientViewer.subjectConsent.deleteDialog.cancel',
    defaultMessage: 'Cancel',
  },
  deletionSuccess: {
    id: 'patientViewer.subjectConsent.deletionSuccess',
    defaultMessage: 'Successfully removed the consent',
  },
  deletionFailure: {
    id: 'patientViewer.subjectConsent.deletionFailure',
    defaultMessage: 'Error occurred while removing the consent',
  },
  deleteConsent: {
    id: 'patientViewer.subjectConsent.deleteConsent',
    defaultMessage: 'Remove consent',
  },
});

class DeleteConsentButton extends PureComponent {
  state = {
    isDialogOpen: false,
  };

  handleDialogOpen = () => {
    this.setState({ isDialogOpen: true });
  };

  handleDialogClose = () => {
    this.setState({ isDialogOpen: false });
  };

  componentDidUpdate(prevProps) {
    if (prevProps.isFetching && !this.props.isFetching && !this.props.error) {
      this.props.onConsentDelete(this.props.directiveId);
    }
  }

  handleDeleteConfirm = () => {
    const {
      deleteConsentDirective,
      directiveId,
      contextKey,
      intl: { formatMessage },
    } = this.props;

    const notification = new NotificationHelper(
      formatMessage(messages.deletionSuccess),
      formatMessage(messages.deletionFailure),
    );

    deleteConsentDirective(directiveId, contextKey, notification);
    this.handleDialogClose();
  };

  render() {
    const {
      isFetching,
      directiveId,
      intl: { formatMessage },
      consentName,
      consentDate,
      isAccepted,
    } = this.props;

    if (!directiveId) return null;

    const title = isAccepted
      ? formatMessage(messages.titleActive)
      : formatMessage(messages.titleProposed);
    const content = isAccepted
      ? formatMessage(messages.contentActive, {
          name: consentName,
          date: consentDate,
        })
      : formatMessage(messages.contentProposed, {
          name: consentName,
          date: consentDate,
        });

    return (
      <EnforceABAC permission={permissions.deleteData}>
        <>
          <IconButton
            aria-label={formatMessage(messages.deleteConsent)}
            aria-haspopup="dialog"
            aria-expanded={this.state.isDialogOpen}
            color="inverse"
            icon={Trash}
            disabled={isFetching}
            onClick={e => {
              e.stopPropagation();
              e.currentTarget.blur();
              this.handleDialogOpen();
            }}
          />
          <Modal
            isOpen={this.state.isDialogOpen}
            title={title}
            onDismiss={this.handleDialogClose}
            actions={
              <React.Fragment>
                <Button variant="text" onClick={this.handleDialogClose}>
                  {formatMessage(messages.cancelButtonText)}
                </Button>
                <Button variant="contained" onClick={this.handleDeleteConfirm}>
                  {formatMessage(messages.acceptButtonText)}
                </Button>
              </React.Fragment>
            }
          >
            <Text>{content}</Text>
          </Modal>
        </>
      </EnforceABAC>
    );
  }
}

DeleteConsentButton.propTypes = {
  directiveId: PropTypes.string.isRequired,
  onConsentDelete: PropTypes.func.isRequired,
  consentName: PropTypes.string.isRequired,
  consentDate: PropTypes.string.isRequired,
  isAccepted: PropTypes.bool.isRequired,
};

const mapDispatchToProps = {
  resetContext: asyncRequestContextsActions.resetContext,
  deleteConsentDirective: consentActions.deleteConsentDirective,
};

const mapStateToProps = (state, props) => {
  const contextKey = contextKeys.patientViewer.deleteConsentDirective(
    props.directiveId,
  );

  const { isFetchingSelector, errorSelector } =
    asyncRequestContextsSelectors.bindContextKeyToSelectors(contextKey);

  return {
    isFetching: isFetchingSelector(state),
    error: errorSelector(state),
    contextKey,
  };
};

export default compose(
  injectIntl,
  connect(mapStateToProps, mapDispatchToProps),
)(DeleteConsentButton);
